/* Collection-only dialog focus assistance. Searchanise retains all app events/state. */
(() => {
  if (!document.body.classList.contains('wwh-collection-page')) return;
  const dialogIds = ['snize-modal-mobile-filters-dialog', 'snize-modal-mobile-sortby-dialog'];
  const filterTrigger = '#product-category .snize-main-panel-mobile-filters > button';
  const sortTrigger = '#product-category .snize-main-panel-dropdown-button';
  let returnSelector = filterTrigger;
  let openedDialog = null;
  let returnTarget = null;
  let scheduled = false;

  const visible = (element) => element && element.isConnected &&
    element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
  const controls = (dialog) => [...dialog.querySelectorAll(
    'button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex="0"]'
  )].filter(visible);

  function syncDialog() {
    scheduled = false;
    const dialog = dialogIds.map((id) => document.getElementById(id)).find(visible);
    if (visible(dialog)) {
      if (!openedDialog) {
        returnSelector = dialog.id === dialogIds[0] ? filterTrigger : sortTrigger;
        returnTarget = document.querySelector(returnSelector);
        openedDialog = dialog;
        dialog.setAttribute('tabindex', '-1');
        (controls(dialog)[0] || dialog).focus({ preventScroll: true });
      } else {
        openedDialog = dialog;
        // Facet updates may replace the focused control. Recover only lost focus.
        if (document.activeElement === document.body) {
          (controls(dialog)[0] || dialog).focus({ preventScroll: true });
        }
      }
    } else if (openedDialog) {
      openedDialog = null;
      const trigger = visible(returnTarget) ? returnTarget : document.querySelector(returnSelector);
      if (visible(trigger)) trigger.focus({ preventScroll: true });
      returnTarget = null;
    }
  }

  // The app appends/rebuilds its modal outside the collection root.
  new MutationObserver(() => {
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(syncDialog);
    }
  }).observe(document.body, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['class', 'style', 'aria-hidden']
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !visible(openedDialog)) return;
    const items = controls(openedDialog);
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!first) {
      event.preventDefault();
      openedDialog.focus();
    } else if (!openedDialog.contains(active) || active === openedDialog ||
      (event.shiftKey ? active === first : active === last)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  });
  // Escape, closing, applying, and clearing remain handled by Searchanise.
  syncDialog();
})();

/* Collection card enrichment; Searchanise keeps ownership of its cards and data. */
(() => {
  if (!document.body.classList.contains('wwh-collection-page')) return;
  const themeId = String(window.Shopify?.theme?.id || '');
  const themeRole = window.Shopify?.theme?.role;
  if (!/^\d+$/.test(themeId) || !['main', 'unpublished', 'development', 'demo'].includes(themeRole)) return;
  const root = document.querySelector('#product-category.wwh-collection');
  const listTemplate = document.getElementById('wwh-collection-spec-list');
  const rowTemplate = document.getElementById('wwh-collection-spec-row');
  const shippingTemplate = document.getElementById('wwh-collection-spec-shipping');
  const featuresTemplate = document.getElementById('wwh-collection-card-features');
  const featureTemplate = document.getElementById('wwh-collection-card-feature');
  if (!root || !listTemplate || !rowTemplate || !shippingTemplate || !featuresTemplate || !featureTemplate) return;
  const icons = new Map([...document.querySelectorAll('[data-wwh-collection-spec-icon]')]
    .map(template => [template.dataset.wwhCollectionSpecIcon, template]));
  const labels = document.getElementById('wwh-collection-spec-labels')?.content;
  let verifiedGasFlows = {};
  try {
    const audit = JSON.parse(document.getElementById('wwh-tankless-gas-verification')?.textContent || '{}');
    if (audit && !Array.isArray(audit) && typeof audit === 'object') verifiedGasFlows = audit;
  } catch { /* Unavailable audit evidence keeps gas tankless on Shopify fallback. */ }
  const fields = {
    capacity: 'metafield_0c4e807c4a8b6839e91a51d81b704e92',
    btu: 'filter_metafield_b514878e5b26dc353ff9d124d6047a87',
    flow: 'filter_metafield_90f3e771845a1d6d879eb60c995def2c',
    voltage: 'filter_metafield_f28b36817b6c96c374b76dd7747bd549',
    fuel: 'metafield_e9cbbe6e3f383a0fcd9357074ee1bca9',
    power: 'metafield_670cee0f85f9ee158b7c1508d0aab3c3',
    vent: 'metafield_22d395a3bff3c9cce81c06a12d6778a9',
    nox: 'metafield_d719ff08f017c5b62b12ab8c63619279',
    shipping: 'metafield_850f992189510b781f304ee65dcfa1d3',
    refrigeration: 'metafield_d38f3afeec47a7837fe7df8feb7cab95',
    mounting: 'metafield_f6da297a64f9e15b1c843aca7756447a',
    activation: 'metafield_03c0ceb5eecb6e4a72f190a36fc4a887',
    oasisType: 'metafield_5ff3be451ec3ae285153619ff435fc79'
  };
  const cache = new Map();
  const states = new WeakMap();
  const observed = new Set();
  const pending = new Set();
  const queue = [];
  const canceled = Symbol('canceled');
  const cacheKey = 'wwh-card-specs:' + themeId + ':v3:' + (window.Shopify.routes?.root || '/') + ':' + document.documentElement.lang;
  const stored = new Map();
  let active = 0;
  let bound = false;
  try {
    const saved = JSON.parse(sessionStorage.getItem(cacheKey) || '[]');
    if (Array.isArray(saved)) saved.slice(-200).forEach(entry => {
      if (Array.isArray(entry) && typeof entry[0] === 'string' && entry[1]?.expires > Date.now()) stored.set(...entry);
    });
  } catch { /* Storage may be disabled; the in-memory cache still works. */ }

  function validData(data, id) {
    if (!data || typeof data.product_id !== 'string' || data.product_id !== id || !Array.isArray(data.specifications) || typeof data.free_shipping !== 'boolean') return null;
    const rows = data.specifications.slice(0, 3).filter(row => row && icons.has(row.type) &&
      typeof row.label === 'string' && typeof row.value === 'string' &&
      row.label.trim() && row.value.trim() && row.label.length <= 100 && row.value.length <= 200);
    const featureTypes = new Set();
    const features = (Array.isArray(data.features) ? data.features : []).slice(0, 2).filter(feature => {
      if (!feature || !['vent', 'nox'].includes(feature.type) || featureTypes.has(feature.type) ||
          typeof feature.value !== 'string' || !feature.value.trim() || feature.value.length > 80) return false;
      featureTypes.add(feature.type);
      return true;
    });
    return { rows, features, freeShipping: data.free_shipping === true };
  }

  function indexedSpecifications(item, id) {
    if (!labels || typeof item.product_type !== 'string' || typeof item.tags !== 'string') return null;
    const values = Object.fromEntries(Object.entries(fields).map(([name, key]) => [name, item[key]]));
    if (Object.values(values).some(value => value != null && typeof value !== 'string')) return null;
    if (Object.values(values).some(value => typeof value === 'string' && value.includes('[:ATTR:]'))) return null;
    const handleize = value => (value || '').trim().toLowerCase().replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const text = value => typeof value === 'string' && !['', 'n/a', 'unknown', 'none', '-'].includes(value.trim().toLowerCase()) ? value.trim() : '';
    const number = value => typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value) && Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : 0;
    const productType = handleize(item.product_type);
    const tags = item.tags.split('[:ATTR:]');
    if (!productType && !tags.some(tag => tag.trim())) return null;
    const oasisType = handleize(values.oasisType);
    const oasis = ['drinking-fountain', 'bottle-filling-station'].includes(oasisType) || ['drinking-fountains', 'fountain', 'bottlefillingstation'].includes(productType);
    const storage = productType.includes('storage-tank');
    const tankless = productType.includes('tankless') || tags.includes('Tankless') || tags.includes('Commercial Tankless');
    let tanklessKind = '';
    if (!oasis && !storage && tankless) {
      const unsupported = /(?:^|-)(?:parts?|accessor(?:y|ies)|kits?|bundles?|boilers?|combi|used|refurbished|open-box|mws-apo-generated)(?:-|$)/;
      if ([productType, ...tags.map(handleize)].some(value => unsupported.test(value))) return null;
      if (!number(values.capacity)) {
        if (text(values.capacity) || item.variantCount !== 1) return null;
        const fuelType = handleize(values.fuel);
        const electricEvidence = productType.includes('electric') || tags.some(tag => ['electric', 'electric-heaters'].includes(handleize(tag)));
        const gasEvidence = /(?:^|-)gas(?:-|$)/.test(productType) || tags.some(tag => ['gas', 'natural-gas', 'liquid-propane', 'propane'].includes(handleize(tag)));
        const supportedVoltage = /^[1-9]\d*(?:\s*[\/,-]\s*[1-9]\d*)*\s*V(?:AC)?$/i.test(text(values.voltage));
        if (fuelType === 'electric' && number(values.power) && supportedVoltage && !text(values.btu) && !gasEvidence) {
          tanklessKind = 'electric';
        } else if (['natural-gas', 'liquid-propane', 'propane', 'natural-gas-propane', 'natural-gas-propane-convertible'].includes(fuelType) &&
            !electricEvidence && !text(values.power) && Number.isInteger(number(values.btu)) && number(values.btu) &&
            number(values.flow) && verifiedGasFlows[id] === number(values.flow)) {
          tanklessKind = 'gas';
        } else return null;
      }
    }
    const electric = values.fuel?.trim().toLowerCase() === 'electric' || productType.includes('electric') || (number(values.power) > 0 && !values.btu);
    const specifications = [];
    function row(source, type, numeric = false, grouped = false) {
      const label = labels.querySelector(`[data-spec-type="${type}"]`);
      const value = numeric ? number(values[source]) : text(values[source]);
      if (!value || !label) return;
      let displayed = numeric ? values[source].replace(/^0+(?=\d)/, '').replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '') : value;
      if (grouped) {
        const parts = displayed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        displayed = parts.join('.');
      }
      if (numeric && label.dataset.unit) displayed += ' ' + label.dataset.unit;
      specifications.push({ type, label: label.textContent, value: displayed });
    }
    if (oasis) {
      row('refrigeration', 'refrigeration');
      row('mounting', 'mounting');
      row('activation', 'activation');
    } else if (storage) {
      row('capacity', 'tank', true, true);
    } else if (tanklessKind === 'gas') {
      row('flow', 'flow', true);
      row('btu', 'gauge', true, true);
      row('fuel', 'flame');
    } else if (tanklessKind === 'electric') {
      row('power', 'power', true);
      row('voltage', 'voltage');
    } else if (electric) {
      row('capacity', 'tank', true, true);
      row('power', 'power', true);
      row('voltage', 'voltage');
    } else {
      row('capacity', 'tank', true, true);
      row('btu', 'gauge', true, true);
      row('fuel', 'flame');
    }
    const features = [];
    const gas = tags.includes('Commercial Water Heater') || ['commercial-gas-water-heater', 'commercial-gas-water-heaters'].includes(productType);
    const gasFuel = ['natural-gas', 'liquid-propane', 'propane', 'natural-gas-propane'].includes(handleize(values.fuel));
    const excluded = ['electric', 'tankless', 'storage', 'fountain'].some(type => productType.includes(type)) || productType === 'mws-apo-generated' ||
      ['Tankless', 'Commercial Tankless', 'Commercial Storage Tanks', 'Electric', 'Electric Heaters'].some(tag => tags.includes(tag));
    if (gas && gasFuel && !excluded) {
      for (const [type, allowed] of [['vent', ['atmospheric-vent', 'power-vent', 'direct-vent', 'power-direct-vent']], ['nox', ['low-nox', 'ultra-low-nox']]]) {
        const key = handleize(values[type]);
        const label = allowed.includes(key) && labels.querySelector(`[data-feature-type="${key}"]`);
        if (label) features.push({ type, value: label.textContent });
      }
    }
    if (values.shipping != null && !['true', 'false'].includes(values.shipping)) return null;
    return validData({ product_id: id, specifications, features, free_shipping: values.shipping === 'true' }, id);
  }

  function cached(key) {
    if (cache.has(key)) return cache.get(key);
    const saved = stored.get(key);
    if (saved?.expires > Date.now()) {
      const data = validData(saved.data, key.split(':')[0]);
      if (data) {
        const result = Promise.resolve(data);
        cache.set(key, result);
        return result;
      }
    }
    stored.delete(key);
    return null;
  }

  function remember(key, data) {
    stored.delete(key);
    stored.set(key, { expires: Date.now() + 300000, data });
    while (stored.size > 200) stored.delete(stored.keys().next().value);
    try { sessionStorage.setItem(cacheKey, JSON.stringify([...stored])); } catch { /* Memory cache remains available. */ }
  }

  function current(state) {
    const product = state && identity(state.card);
    return state && state.card.isConnected && states.get(state.card) === state &&
      state.card.querySelector('.snize-overhidden') === state.caption && product?.key === state.key && product.signature === state.signature;
  }

  function nearby(card) {
    const rect = card.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom >= -400 && rect.top <= innerHeight + 400;
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) start(entry.target); });
  }, { rootMargin: '400px 0px' }) : null;

  function observe(state) {
    observed.add(state.card);
    if (observer) observer.observe(state.card);
    else start(state.card);
  }

  function drain() {
    while (active < 2 && queue.length) {
      const task = queue.shift();
      active++;
      task().finally(() => { active--; drain(); });
    }
  }

  function specifications({ id, handle, key }) {
    const saved = cached(key);
    if (saved) return saved;
    const result = new Promise(resolve => {
      queue.push(async () => {
        if (![...pending].some(state => state.key === key && current(state) && (!observer || nearby(state.card)))) {
          cache.delete(key);
          resolve(canceled);
          return;
        }
        const url = new URL((window.Shopify.routes?.root || '/') + 'products/' + handle, location.origin);
        url.searchParams.set('view', 'wwh-card-specs');
        if (themeRole !== 'main') url.searchParams.set('preview_theme_id', themeId);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        try {
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('Specification response unavailable');
          const data = await response.json();
          const valid = validData(data, id);
          if (!valid) throw new Error('Invalid specification response');
          remember(key, data);
          resolve(valid);
        } catch {
          // One attempt per product/page visit; failure leaves the native card usable.
          resolve(null);
        } finally {
          clearTimeout(timeout);
        }
      });
    });
    cache.set(key, result);
    drain();
    return result;
  }

  function identity(card) {
    const item = window.Searchanise.$(card).data('snize_item_data');
    const id = String(item?.original_product_id || '');
    if (!/^\d+$/.test(id) || (card.dataset.originalProductId && card.dataset.originalProductId !== id)) return null;
    try {
      const url = new URL(card.querySelector('a.snize-view-link')?.href || item.link, location.href);
      if (url.origin !== location.origin) return null;
      const handle = decodeURIComponent(url.pathname.match(/\/products\/([^/]+)\/?$/)?.[1] || '');
      if (!/^[a-z0-9][a-z0-9_-]{0,254}$/i.test(handle)) return null;
      if (item.link) {
        const indexed = new URL(item.link, location.href);
        if (indexed.origin !== location.origin || decodeURIComponent(indexed.pathname.match(/\/products\/([^/]+)\/?$/)?.[1] || '') !== handle) return null;
      }
      const specificationItem = Object.fromEntries(['product_type', 'tags', ...Object.values(fields)].map(key => [key, item[key]]));
      // Configuration count is metadata only; variant prices never enter the cache/signature.
      specificationItem.variantCount = Array.isArray(item.shopify_variants) ? item.shopify_variants.length : null;
      const signature = JSON.stringify(specificationItem);
      return { id, handle, key: id + ':' + handle, item: specificationItem, signature };
    } catch { return null; }
  }

  function start(card) {
      const state = states.get(card);
      if (!state || !current(state) || state.pending || state.loaded) return;
      observer?.unobserve(card);
      observed.delete(card);
      const { handle, caption, title } = state;
      const slot = document.createElement('div');
      slot.className = 'wwh-collection-card__specifications';
      slot.dataset.wwhCardSpecs = handle;
      slot.dataset.loading = 'true';
      (caption.querySelector('.wwh-collection-card__metadata') || title).after(slot);
      state.slot = slot;
      state.pending = true;
      pending.add(state);
      const indexed = indexedSpecifications(state.item, state.id);
      (cached(state.key) || (indexed ? Promise.resolve(indexed) : specifications(state))).then(data => {
        pending.delete(state);
        if (!current(state) || !caption.contains(slot)) return;
        state.pending = false;
        delete slot.dataset.loading;
        if (data === canceled) {
          slot.remove();
          state.slot = null;
          observe(state);
          return;
        }
        state.loaded = true;
        if (data?.rows.length || data?.features.length) {
          const content = [];
          if (data.rows.length) {
            const list = listTemplate.content.firstElementChild.cloneNode(true);
            data.rows.forEach(row => {
              const element = rowTemplate.content.firstElementChild.cloneNode(true);
              element.prepend(icons.get(row.type).content.firstElementChild.cloneNode(true));
              element.querySelector('[data-wwh-spec-label]').textContent = row.label + ': ';
              element.querySelector('[data-wwh-spec-value]').textContent = row.value;
              list.append(element);
            });
            content.push(list);
          }
          if (data.features.length) {
            const features = featuresTemplate.content.firstElementChild.cloneNode(true);
            data.features.forEach(feature => {
              const element = featureTemplate.content.firstElementChild.cloneNode(true);
              element.dataset.wwhFeature = feature.type;
              element.textContent = feature.value;
              features.append(element);
            });
            content.push(features);
          }
          slot.replaceChildren(...content);
        } else {
          slot.remove();
          state.slot = null;
        }
        if (data?.freeShipping === true) {
          const badge = shippingTemplate.content.firstElementChild.cloneNode(true);
          badge.dataset.wwhCardShipping = handle;
          // Normal flow below the image avoids collisions with native image labels.
          caption.prepend(badge);
          state.badge = badge;
        }
      });
  }

  function update() {
    observed.forEach(card => {
      if (!card.isConnected || !current(states.get(card))) {
        observer?.unobserve(card);
        observed.delete(card);
      }
    });
    root.querySelectorAll('#snize_results .snize-product').forEach(card => {
      const product = identity(card);
      const caption = card.querySelector('.snize-overhidden');
      const title = caption?.querySelector('.snize-title');
      const previous = states.get(card);
      if (product && title && previous?.key === product.key && previous.signature === product.signature && previous.caption === caption &&
          (!previous.slot || caption.contains(previous.slot)) && (!previous.badge || caption.contains(previous.badge))) return;
      observer?.unobserve(card);
      observed.delete(card);
      states.delete(card);
      card.querySelectorAll('[data-wwh-card-specs], [data-wwh-card-shipping]').forEach(node => node.remove());
      if (!product || !title) return;
      const state = { ...product, card, caption, title, pending: false, loaded: false };
      states.set(card, state);
      if (indexedSpecifications(product.item, product.id) || cached(product.key)) start(card);
      else observe(state);
    });
  }

  function bind() {
    if (bound || !window.Searchanise?.$) return;
    bound = true;
    window.Searchanise.$(document).on('Searchanise.ResultsUpdated.wwhCardSpecs', update);
    update();
  }
  document.addEventListener('Searchanise.Loaded', bind);
  bind();
})();

/* Collection metadata comes only from the app's indexed product record. */
(() => {
  if (!document.body.classList.contains('wwh-collection-page')) return;
  const root = document.querySelector('#product-category.wwh-collection');
  if (!root) return;
  let bound = false;
  const text = (value) => typeof value === 'string' ? value.trim() : '';

  function primarySku(item) {
    const code = text(item.product_code);
    const variants = Array.isArray(item.shopify_variants) ? item.shopify_variants : [];
    const skus = [...new Set(variants.map((variant) => text(variant.sku)).filter(Boolean))];
    if (!code) return skus.length === 1 ? skus[0] : '';
    if (skus.length <= 1) return !skus.length || skus[0] === code ? code : '';
    // Multiple SKUs are safe only when the indexed code identifies the app's default variant.
    const primary = variants.find((variant) => String(variant.variant_id) === String(item.add_to_cart_id));
    return primary && text(primary.sku) === code ? code : '';
  }

  function updateMetadata() {
    root.querySelectorAll('#snize_results .snize-product').forEach((card) => {
      const item = window.Searchanise.$(card).data('snize_item_data');
      const title = card.querySelector('.snize-overhidden > .snize-title');
      if (!item || !title) return;
      const vendor = text(item.vendor);
      const sku = primarySku(item);
      let row = card.querySelector('.wwh-collection-card__metadata');
      if (!vendor && !sku) {
        row?.remove();
        return;
      }
      const key = JSON.stringify([vendor, sku]);
      if (row && row.wwhMetadataKey === key) return;
      if (!row) {
        row = document.createElement('span');
        row.className = 'wwh-collection-card__metadata';
        title.after(row);
      }
      const content = [];
      if (vendor) {
        const manufacturer = document.createElement('span');
        manufacturer.className = 'wwh-collection-card__manufacturer';
        manufacturer.setAttribute('translate', 'no');
        manufacturer.textContent = vendor;
        content.push(manufacturer);
      }
      if (vendor && sku) {
        const separator = document.createElement('span');
        separator.className = 'wwh-collection-card__metadata-separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = ' \u2022 ';
        content.push(separator);
      }
      if (sku) {
        const label = document.createElement('span');
        label.className = 'wwh-collection-card__sku';
        label.setAttribute('translate', 'no');
        label.textContent = `SKU: ${sku}`;
        content.push(label);
      }
      row.replaceChildren(...content);
      row.wwhMetadataKey = key;
    });
  }

  function bind() {
    if (bound || !window.Searchanise?.$) return;
    bound = true;
    window.Searchanise.$(document).on('Searchanise.ResultsUpdated.wwhCollectionMetadata', updateMetadata);
    updateMetadata();
  }
  document.addEventListener('Searchanise.Loaded', bind);
  bind();
})();
